import { useCallback, useEffect, useState } from "react";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Spinner from "../common/Spinner";
import { getLeadCampaign, saveLeadCampaign, previewLeadCampaign, testLeadCampaign } from "../../api/email";

// The no-reply follow-ups: written once here, then drafted on schedule for
// leads who go quiet after your first reply. Each waits for your approval.

const MAX_STEPS = 6;

export default function LeadCampaignPanel({ onNotice, onError }) {
  const [data, setData] = useState(null);
  const [steps, setSteps] = useState([]);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null); // {subject, html}

  const apply = useCallback((d) => {
    setData(d);
    setSteps(d.settings.steps.map((s) => ({ ...s })));
  }, []);

  useEffect(() => {
    getLeadCampaign()
      .then(apply)
      .catch((e) => onError(e.message || "Couldn't load the no-reply follow-ups."));
  }, [apply, onError]);

  if (!data) {
    return (
      <div className="py-6 flex justify-center">
        <Spinner label="Loading..." />
      </div>
    );
  }

  const s = data.settings;
  const dirty = JSON.stringify(steps) !== JSON.stringify(s.steps);

  async function save(patch, message) {
    setBusy(Object.keys(patch)[0]);
    onError("");
    try {
      apply(await saveLeadCampaign(patch));
      if (message) onNotice(message);
      return true;
    } catch (e) {
      onError(e.message || "Couldn't save.");
      return false;
    } finally {
      setBusy("");
    }
  }

  async function toggle(on) {
    if (on) {
      if (dirty && !(await save({ steps }))) return;
      const ok = window.confirm(
        "Turn on the no-reply follow-ups?\n\nFrom now on, when a lead you've replied to goes quiet, each of these " +
          "is drafted on its day and waits in Follow-ups. Nothing is sent until you approve it."
      );
      if (!ok) return;
    }
    save(
      { enabled: on },
      on ? "No-reply follow-ups are on." : "No-reply follow-ups are off. No more will be drafted."
    );
  }

  const setStep = (i, key, value) => setSteps((all) => all.map((st, j) => (j === i ? { ...st, [key]: value } : st)));

  async function show(i) {
    setBusy(`preview-${i}`);
    try {
      setPreview(await previewLeadCampaign(i, steps));
    } catch (e) {
      onError(e.message || "Couldn't show a preview.");
    } finally {
      setBusy("");
    }
  }

  async function test(i) {
    setBusy(`test-${i}`);
    onError("");
    try {
      const r = await testLeadCampaign(i, steps);
      onNotice(`Test of email ${i + 1} sent to ${r.to}.`);
    } catch (e) {
      onError(e.message || "Couldn't send the test.");
    } finally {
      setBusy("");
    }
  }

  function addStep() {
    const last = steps[steps.length - 1];
    setSteps((all) => [
      ...all,
      { day: (last?.day || 0) + 14, subject: "", body: "Hi {first_name},\n\n\n\n{signature}", button: true },
    ]);
  }

  const st = data.stats;
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm text-birdie-charcoal/80 space-y-1">
          <p>
            When a lead you've replied to goes quiet, each of these is drafted on its day and waits in{" "}
            <strong>Follow-ups</strong> for you to edit and send, or skip. Nothing goes without your approval. After
            the last one is sent, they join your newsletter.
          </p>
          <p className="text-xs text-birdie-charcoal/55">
            Drafted from {data.draft_hour}am Eastern, never within two days of another email to them. They stop, and a
            waiting one is withdrawn, as soon as you mark <strong>They replied</strong>, the lead books or is marked
            lost, or they unsubscribe. Leads you replied to before switching on aren't included; start them from their
            conversation.
          </p>
        </div>
        <Switch checked={Boolean(s.enabled)} onChange={toggle} label="Draft no-reply follow-ups" disabled={Boolean(busy)} />
      </div>

      {!data.mailing_address_set && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Add your mailing address in Settings below first. The law requires it in these emails.
        </div>
      )}

      {(st.active > 0 || st.finished > 0 || st.replied > 0) && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Running now" value={st.active} />
          <Stat label="Replied" value={st.replied} />
          <Stat label="Booked" value={st.booked} />
          <Stat label="Finished" value={st.finished} />
        </div>
      )}

      <ol className="space-y-3">
        {steps.map((step, i) => (
          <li key={i} className="rounded-xl border border-birdie-green/15 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-birdie-charcoal">
                Email {i + 1}
                <label className="flex items-center gap-1 text-xs font-normal text-birdie-charcoal/60">
                  on day
                  <input
                    type="number"
                    min={1}
                    max={180}
                    value={step.day}
                    onChange={(e) => setStep(i, "day", Number(e.target.value))}
                    className="w-16 rounded-lg border border-birdie-green/20 px-2 py-1 text-sm"
                    aria-label={`Email ${i + 1} day`}
                  />
                </label>
              </div>
              {steps.length > 1 && (
                <button
                  type="button"
                  onClick={() => setSteps((all) => all.filter((_, j) => j !== i))}
                  className="text-[11px] font-semibold text-birdie-charcoal/45 hover:text-red-700"
                >
                  Remove
                </button>
              )}
            </div>
            <input
              value={step.subject}
              onChange={(e) => setStep(i, "subject", e.target.value)}
              className={INPUT}
              maxLength={200}
              placeholder="Subject"
              aria-label={`Email ${i + 1} subject`}
            />
            <textarea
              value={step.body}
              onChange={(e) => setStep(i, "body", e.target.value)}
              rows={7}
              className={`${INPUT} leading-relaxed`}
              aria-label={`Email ${i + 1} message`}
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="flex items-center gap-2 text-xs text-birdie-charcoal/70">
                <input
                  type="checkbox"
                  checked={Boolean(step.button)}
                  onChange={(e) => setStep(i, "button", e.target.checked)}
                  className="h-4 w-4 min-h-0 min-w-0 accent-birdie-green"
                />
                Button: their quote if they have one, otherwise “Check a date”
              </label>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => show(i)} disabled={Boolean(busy)}>
                  {busy === `preview-${i}` ? "…" : "Preview"}
                </Button>
                <Button size="sm" variant="secondary" onClick={() => test(i)} disabled={Boolean(busy)}>
                  {busy === `test-${i}` ? "Sending…" : "Send me a test"}
                </Button>
              </div>
            </div>
          </li>
        ))}
      </ol>
      <p className="text-[11px] text-birdie-charcoal/50">
        Days count from your first reply; if you approve one late, the next keeps the same gap.{" "}
        <code>{"{first_name}"}</code>, <code>{"{event}"}</code> (“holiday party”),{" "}
        <code>{"{date_phrase}"}</code> (“ on Saturday, December 12”, or nothing if their date is unclear or past),{" "}
        <code>{"{signature}"}</code> and <code>[button]</code> are filled in for each lead. If their date passes, they
        skip to the last email. Skipping the last one means they don't join the newsletter, since it's the email
        that tells them.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => save({ steps }, "Saved.")} disabled={!dirty || Boolean(busy)}>
          {busy === "steps" ? "Saving…" : "Save emails"}
        </Button>
        {steps.length < MAX_STEPS && (
          <Button size="sm" variant="secondary" onClick={addStep} disabled={Boolean(busy)}>
            + Add an email
          </Button>
        )}
        {dirty && (
          <Button size="sm" variant="ghost" onClick={() => setSteps(s.steps.map((x) => ({ ...x })))}>
            Undo changes
          </Button>
        )}
        <div className="flex-1" />
        <button
          type="button"
          onClick={() =>
            window.confirm("Replace your emails with the suggested ones?") &&
            save({ reset_steps: true }, "Back to the suggested emails.")
          }
          className="text-[11px] font-semibold text-birdie-charcoal/45 hover:text-birdie-charcoal"
        >
          Use the suggested emails
        </button>
      </div>

      <label className="flex items-center justify-between gap-3 border-t border-birdie-green/10 pt-3">
        <span className="min-w-0">
          <span className="block text-sm text-birdie-charcoal">Add them to the newsletter after the last email is sent</span>
          <span className="block text-[11px] text-birdie-charcoal/50">
            The last email tells them so, and every newsletter has an unsubscribe link.
          </span>
        </span>
        <Switch
          checked={s.join_list !== false}
          onChange={(v) => save({ join_list: v })}
          label="Add them to the newsletter"
          disabled={Boolean(busy)}
        />
      </label>

      <Modal isOpen={Boolean(preview)} title={preview?.subject || "Preview"} onClose={() => setPreview(null)} maxWidthClass="max-w-2xl">
        {preview && (
          <>
            <iframe
              title="Email preview"
              srcDoc={preview.html}
              sandbox=""
              className="w-full h-[460px] rounded-xl border border-birdie-green/15 bg-birdie-cream"
            />
            <p className="text-[11px] text-birdie-charcoal/45 mt-1">Shown for a sample lead: Dana, a holiday party.</p>
          </>
        )}
      </Modal>
    </div>
  );
}

const INPUT =
  "w-full rounded-xl border border-birdie-green/20 bg-white px-3 py-2 text-sm text-birdie-charcoal focus:outline-none focus:ring-2 focus:ring-birdie-mustard/40";

function Stat({ label, value }) {
  return (
    <div className="bg-birdie-cream/70 rounded-xl px-3 py-2">
      <div className="text-[11px] text-birdie-charcoal/55">{label}</div>
      <div className="text-lg font-semibold text-birdie-charcoal">{Number(value || 0).toLocaleString()}</div>
    </div>
  );
}

function Switch({ checked, onChange, label, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative h-6 w-11 min-w-0 min-h-0 shrink-0 rounded-full transition disabled:opacity-60 ${
        checked ? "bg-birdie-green" : "bg-birdie-charcoal/20"
      }`}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${checked ? "left-[22px]" : "left-0.5"}`}
      />
    </button>
  );
}
